import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  formatBrandMoney,
  getBrandLocale,
  getBrandPriceTierThresholds,
  getBrandVatRate,
  getBrandVatMultiplier,
  getBrandCurrencySuffix,
  amountExclVat,
  vatFromTtc,
} from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { makePageT } from '../../shared/utils/pageI18n';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Tag, Search, Plus, Edit, Trash2, RefreshCcw, TrendingUp, TrendingDown, Filter, Eye, X, XCircle, LayoutGrid, List, LayoutList, Upload, Download, FileSpreadsheet, FileText, ChevronDown, ChevronUp, CheckCircle, DollarSign, Package, Users, ShoppingCart, AlertCircle, Percent, Layers, Clock, Target, Award, MessageSquare, Calendar, Gift } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import '../../shared/components/custom/CustomComponents.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import VentesListPageChrome, { facturationTableScrollClass } from './components/VentesListPageChrome';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  mapVentesStatCards,
  useCrudModalBodyLock,
  initVentesColumnVisibility,
  buildVentesQawaemColumnDefs,
  isVentesColumnVisible,
  buildVentesBulkDeleteFilterAction,
  computeVentesRowSelection,
  resolveVentesEntityReference,
  useVentesEntityReference,
  VENTES_REF_INPUT_CLASS,
  VentesConfigNotice,
  sliceVentesListItems,
  matchesVentesListDateRange,
  resolveCustomerSegmentLabel,
  normalizeCustomerSegmentCode,
} from './ventesListPageUtils';
import { useVentesConfig } from './useVentesConfig';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const PrixRemisesPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.ventes.pages.prixRemisesPage');
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const priceTiers = getBrandPriceTierThresholds();
  const vatRate = getBrandVatRate();
  const currencySuffix = getBrandCurrencySuffix();
  const amountStep = priceTiers.lowMax >= 10000 ? 1000 : 100;

  const getStatusLabel = (status) => {
    const map = {
      active: p('status_active'),
      scheduled: p('status_scheduled'),
      expired: p('status_expired'),
      inactive: p('status_inactive'),
    };
    return map[status] || status || p('em_dash');
  };

  const getRuleTypeLabel = (type) => {
    const map = {
      discount: p('rule_type_discount'),
      special_price: p('rule_type_special_price'),
      promotion: p('rule_type_promotion'),
      volume_pricing: p('rule_type_volume'),
      seasonal: p('rule_type_seasonal'),
    };
    return map[type] || type || p('em_dash');
  };

  const getDiscountTypeLabel = (type) => {
    const map = {
      percentage: p('discount_type_percentage'),
      fixed_amount: p('discount_type_fixed'),
      buy_x_get_y: p('discount_type_buy_x'),
      free_shipping: p('discount_type_free_shipping'),
    };
    return map[type] || type || p('em_dash');
  };

  const getPriorityLabel = (priority) => {
    const map = {
      low: p('priority_low'),
      medium: p('priority_medium'),
      high: p('priority_high'),
      critical: p('priority_critical'),
    };
    return map[priority] || priority || p('em_dash');
  };

  const ruleTypeOptions = useMemo(
    () => [
      { value: 'discount', label: p('rule_type_discount') },
      { value: 'special_price', label: p('rule_type_special_price') },
      { value: 'promotion', label: p('rule_type_promotion') },
      { value: 'volume_pricing', label: p('rule_type_volume') },
      { value: 'seasonal', label: p('rule_type_seasonal') },
    ],
    [t],
  );

  const discountTypeOptions = useMemo(
    () => [
      { value: 'percentage', label: p('discount_type_percentage') },
      { value: 'fixed_amount', label: p('discount_type_fixed') },
      { value: 'buy_x_get_y', label: p('discount_type_buy_x') },
      { value: 'free_shipping', label: p('discount_type_free_shipping') },
    ],
    [t],
  );

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: p('status_active') },
      { value: 'scheduled', label: p('status_scheduled') },
      { value: 'expired', label: p('status_expired') },
      { value: 'inactive', label: p('status_inactive') },
    ],
    [t],
  );

  const priorityOptions = useMemo(
    () => [
      { value: 'low', label: p('priority_low') },
      { value: 'medium', label: p('priority_medium') },
      { value: 'high', label: p('priority_high') },
      { value: 'critical', label: p('priority_critical') },
    ],
    [t],
  );

  const stackableOptions = useMemo(
    () => [
      { value: 'yes', label: p('yes') },
      { value: 'no', label: p('no') },
    ],
    [t],
  );

  const priceIncludesVatOptions = useMemo(
    () => [
      { value: 'exclusive', label: p('price_vat_exclusive') },
      { value: 'inclusive', label: p('price_vat_inclusive') },
    ],
    [t],
  );

  const volumeTierHint = useMemo(
    () => p('hint_volume_tiers', {
      lowMax: formatBrandMoney(priceTiers.lowMax, false),
      highMin: formatBrandMoney(priceTiers.highMin, false),
      suffix: currencySuffix,
    }),
    [p, priceTiers, currencySuffix],
  );

  const productCategoryOptions = useMemo(
    () => [
      { value: 'electronics', label: p('category_electronics') },
      { value: 'clothing', label: p('category_clothing') },
      { value: 'food', label: p('category_food') },
    ],
    [t],
  );

  const customerSegmentOptions = useMemo(
    () => [
      { value: 'vip', label: p('segment_vip') },
      { value: 'wholesale', label: p('segment_wholesale') },
      { value: 'retail', label: p('segment_retail') },
    ],
    [t],
  );

  const getCustomerSegmentLabel = useCallback(
    (segment) => resolveCustomerSegmentLabel(segment, customerSegmentOptions) || p('em_dash'),
    [customerSegmentOptions, p],
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRules, setPriceRules] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [selectedRules, setSelectedRules] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const ventesConfig = useVentesConfig();
  const { dateRange } = useSaudiListDateRange();

  useEffect(() => {
    setViewMode(ventesConfig.defaultView || 'table');
  }, [ventesConfig.defaultView]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRule, setViewingRule] = useState(null);
  const [availableClients, setAvailableClients] = useState([]);

  useCrudModalBodyLock(showModal, showViewModal);
  const { settings } = useSettings();
  const generatePriceRuleRef = useVentesEntityReference('priceRule', priceRules);
  
  // Collapsible sections state
  const [formTab, setFormTab] = useState('rule');
  
  const [formData, setFormData] = useState({ 
    rule_reference: '', 
    rule_name: '',
    rule_type: 'discount',
    discount_type: 'percentage',
    status: 'active',
    product_name: '',
    product_category: '',
    customer_name: '',
    customer_id: '',
    customer_segment: '',
    priority: 'medium',
    base_price: '',
    discount_percentage: '',
    discount_amount: '',
    final_price: '',
    min_quantity: '',
    max_quantity: '',
    min_amount: '',
    max_amount: '',
    start_date: '',
    end_date: '',
    valid_days: '',
    usage_limit: '',
    usage_count: '',
    stackable: 'no',
    promo_code: '',
    conditions: '',
    notes: '',
    created_by: '',
    approved_by: '',
    price_includes_vat: 'exclusive',
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_rules');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  const priceFormTabs = useMemo(() => ([
    { id: 'rule', label: p('section_basic_info'), icon: Tag, iconColor: 'text-blue-400' },
    { id: 'pricing', label: p('section_pricing'), icon: DollarSign, iconColor: 'text-green-400' },
    { id: 'conditions', label: p('section_conditions'), icon: Target, iconColor: 'text-purple-400' },
  ]), [p]);

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const thFilterClass = 'text-white font-semibold py-4';

  const tableColumnDefs = useMemo(
    () => [
      { key: 'rule_reference', label: p('col_reference'), required: true },
      { key: 'rule_name', label: p('col_name') },
      { key: 'rule_type', label: p('col_type') },
      { key: 'product_name', label: p('col_product') },
      { key: 'discount_percentage', label: p('col_discount') },
      { key: 'start_date', label: p('col_period') },
      { key: 'usage_count', label: p('col_usage') },
      { key: 'priority', label: p('col_priority') },
      { key: 'status', label: p('status_label') },
    ],
    [p],
  );
  const [columnVisibility, setColumnVisibility] = useState(() =>
    initVentesColumnVisibility(tableColumnDefs.map((c) => c.key)),
  );
  const qawaemColumnDefs = useMemo(
    () => buildVentesQawaemColumnDefs(tableColumnDefs, columnVisibility),
    [tableColumnDefs, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const showCol = (key) => isVentesColumnVisible(columnVisibility, key);
  const tableColSpan = tableColumnDefs.filter((c) => showCol(c.key)).length + 2;

  useEffect(() => { loadPriceRules(); }, []);

  useEffect(() => {
    if (!showModal) return;
    const loadClients = async () => {
      try {
        const response = await localApiAdapter.get('/facturation/clients');
        const rows = response?.data || response?.clients || response || [];
        setAvailableClients(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error('Error loading clients:', error);
        setAvailableClients([]);
      }
    };
    void loadClients();
  }, [showModal]);

  // Auto-calculate final price (Saudi VAT 15% when price is ex-VAT)
  useEffect(() => {
    const base = parseFloat(formData.base_price) || 0;
    if (!base) return;

    let net = base;
    if (formData.discount_type === 'percentage' && formData.discount_percentage) {
      net = base * (1 - (parseFloat(formData.discount_percentage) || 0) / 100);
    } else if (formData.discount_type === 'fixed_amount' && formData.discount_amount) {
      net = base - (parseFloat(formData.discount_amount) || 0);
    }
    net = Math.max(0, net);

    const finalPrice = formData.price_includes_vat === 'inclusive'
      ? net
      : net * getBrandVatMultiplier();

    setFormData((prev) => {
      const next = finalPrice.toFixed(2);
      if (prev.final_price === next) return prev;
      return { ...prev, final_price: next };
    });
  }, [
    formData.base_price,
    formData.discount_percentage,
    formData.discount_amount,
    formData.discount_type,
    formData.price_includes_vat,
  ]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPriceRules = async () => {
    try {
      const response = await localApiAdapter.get('/ventes/prix-remises');
      const rows = response?.data || response || [];
      setPriceRules(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading price rules:', error);
      setPriceRules([]);
    }
  };

  const stats = {
    total: priceRules.length,
    active: priceRules.filter(r => r.status === 'active').length,
    expired: priceRules.filter(r => r.status === 'expired').length,
    scheduled: priceRules.filter(r => r.status === 'scheduled').length,
    discounts: priceRules.filter(r => r.rule_type === 'discount').length,
    specialPrices: priceRules.filter(r => r.rule_type === 'special_price').length,
    avgDiscount: priceRules.length > 0 ? (priceRules.filter(r => r.discount_percentage).reduce((sum, r) => sum + (parseFloat(r.discount_percentage) || 0), 0) / priceRules.filter(r => r.discount_percentage).length).toFixed(1) || 0 : 0,
    totalUsage: priceRules.reduce((sum, r) => sum + (parseInt(r.usage_count) || 0), 0)
  };

  const mainTabs = useMemo(() => [
    { id: 'all_rules', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_type', name: p('tab_by_type'), icon: Tag },
    { id: 'par_statut', name: p('tab_by_status'), icon: Target },
  ], [t]);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'active', name: p('quick_active'), icon: CheckCircle },
    { id: 'scheduled', name: p('quick_scheduled'), icon: Clock },
    { id: 'expired', name: p('quick_expired'), icon: XCircle },
  ], [t]);

  const ruleDistribution = useMemo(() => ({
    all_rules: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'scheduled', name: p('dist_scheduled'), count: stats.scheduled, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'expired', name: p('dist_expired'), count: stats.expired, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
    par_type: {
      title: p('dist_by_type'),
      data: [
        { filterKey: 'discount', name: p('dist_discounts'), count: stats.discounts, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'special_price', name: p('dist_special_prices'), count: stats.specialPrices, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'promotion', name: p('dist_promotions'), count: priceRules.filter(r => r.rule_type === 'promotion').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'volume_pricing', name: p('dist_volume'), count: priceRules.filter(r => r.rule_type === 'volume_pricing').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    par_statut: {
      title: p('dist_by_status'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'scheduled', name: p('dist_scheduled'), count: stats.scheduled, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'expired', name: p('dist_expired'), count: stats.expired, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'inactive', name: p('dist_inactive'), count: priceRules.filter(r => r.status === 'inactive').length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
      ],
    },
  }), [priceRules, stats, t]);

  const distributionInfo = ruleDistribution[mainViewMode] || ruleDistribution.all_rules;

  const ruleStatusFilterOptions = useMemo(() => ([
    { value: 'active', label: p('status_active') },
    { value: 'scheduled', label: p('status_scheduled') },
    { value: 'expired', label: p('status_expired') },
    { value: 'inactive', label: p('status_inactive') },
  ]), [p]);

  const columnFilterRules = useMemo(() => ([
    textColumnRule('rule_reference'),
    textColumnRule('rule_name'),
    textColumnRule('rule_type', (r) => getRuleTypeLabel(r.rule_type)),
    textColumnRule('product_name'),
    textColumnRule('discount_percentage', (r) => String(r.discount_percentage ?? '')),
    textColumnRule('start_date', (r) => (r.start_date ? new Date(r.start_date).toLocaleDateString(dateLocale) : '')),
    textColumnRule('usage_count', (r) => String(r.usage_count ?? '')),
    textColumnRule('priority', (r) => String(r.priority ?? '')),
    exactColumnRule('status', (r) => r.status ?? ''),
  ]), [p, dateLocale]);

  const baseFilteredRules = useMemo(() => priceRules.filter(r => {
    if (!matchesVentesListDateRange(r.start_date, dateRange)) return false;
    // Search filter
    if (searchTerm && !r.rule_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.rule_reference?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.product_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Quick / secondary status filter
    if (quickFilter !== 'all') {
      if (quickFilter === 'active' && r.status !== 'active') return false;
      if (quickFilter === 'scheduled' && r.status !== 'scheduled') return false;
      if (quickFilter === 'expired' && r.status !== 'expired') return false;
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_rules':
          if (activeDistributionFilter === 'active' && r.status !== 'active') return false;
          if (activeDistributionFilter === 'scheduled' && r.status !== 'scheduled') return false;
          if (activeDistributionFilter === 'expired' && r.status !== 'expired') return false;
          break;
        case 'par_type':
          if (activeDistributionFilter === 'discount' && r.rule_type !== 'discount') return false;
          if (activeDistributionFilter === 'special_price' && r.rule_type !== 'special_price') return false;
          if (activeDistributionFilter === 'promotion' && r.rule_type !== 'promotion') return false;
          if (activeDistributionFilter === 'volume_pricing' && r.rule_type !== 'volume_pricing') return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'active' && r.status !== 'active') return false;
          if (activeDistributionFilter === 'scheduled' && r.status !== 'scheduled') return false;
          if (activeDistributionFilter === 'expired' && r.status !== 'expired') return false;
          if (activeDistributionFilter === 'inactive' && r.status !== 'inactive') return false;
          break;
      }
    }
    
    return true;
  }), [priceRules, searchTerm, quickFilter, activeDistributionFilter, mainViewMode, dateRange]);

  const filteredRules = useMemo(
    () => applyColumnFilters(baseFilteredRules, filters, columnFilterRules),
    [baseFilteredRules, filters, columnFilterRules],
  );

  const filteredRuleIds = useMemo(() => filteredRules.map((r) => r.id), [filteredRules]);
  const displayedRules = useMemo(
    () => sliceVentesListItems(filteredRules, ventesConfig.itemsPerPage),
    [filteredRules, ventesConfig.itemsPerPage],
  );
  const { allRowsSelected, someRowsSelected } = useMemo(
    () => computeVentesRowSelection(filteredRuleIds, selectedRules),
    [filteredRuleIds, selectedRules],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      ventesConfig.requirePromoCodeApproval
      && String(formData.promo_code || '').trim()
      && !String(formData.approved_by || '').trim()
    ) {
      notifyError(showNotify, p('field_approved_by', { defaultValue: 'موافق عليها من' }));
      return;
    }
    try {
      const ruleReference = resolveVentesEntityReference(
        settings,
        'priceRule',
        priceRules,
        formData.rule_reference,
      );
      const submitData = { 
        rule_reference: ruleReference,
        rule_name: formData.rule_name,
        rule_type: formData.rule_type,
        discount_type: formData.discount_type,
        status: formData.status,
        product_name: formData.product_name,
        product_category: formData.product_category,
        customer_name: formData.customer_name,
        customer_segment: normalizeCustomerSegmentCode(formData.customer_segment),
        priority: formData.priority,
        base_price: parseFloat(formData.base_price) || 0,
        discount_percentage: parseFloat(formData.discount_percentage) || 0,
        discount_amount: parseFloat(formData.discount_amount) || 0,
        final_price: parseFloat(formData.final_price) || 0,
        min_quantity: parseFloat(formData.min_quantity) || 0,
        max_quantity: parseFloat(formData.max_quantity) || 0,
        min_amount: parseFloat(formData.min_amount) || 0,
        max_amount: parseFloat(formData.max_amount) || 0,
        start_date: formData.start_date,
        end_date: formData.end_date,
        valid_days: formData.valid_days,
        usage_limit: parseInt(formData.usage_limit) || 0,
        usage_count: parseInt(formData.usage_count) || 0,
        stackable: formData.stackable,
        promo_code: formData.promo_code,
        conditions: formData.conditions,
        notes: formData.notes,
        created_by: formData.created_by,
        approved_by: formData.approved_by,
        price_includes_vat: formData.price_includes_vat,
        vat_rate: vatRate,
      };
      if (editingRule) {
        await localApiAdapter.put(`/ventes/prix-remises/${editingRule.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'قاعدة سعر' }));
      } else {
        await localApiAdapter.post('/ventes/prix-remises', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'قاعدة سعر' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('error_save', { message: error.message || p('error_unknown') }));
      return;
    } finally {
      resetForm();
      loadPriceRules();
    }
  };

  const handleEdit = (rule) => {
    setFormTab('rule');
    setEditingRule(rule);
    setFormData({ 
      rule_reference: rule.rule_reference || '',
      rule_name: rule.rule_name || '',
      rule_type: rule.rule_type || 'discount',
      discount_type: rule.discount_type || 'percentage',
      status: rule.status || 'active',
      product_name: rule.product_name || '',
      product_category: rule.product_category || '',
      customer_name: rule.customer_name || '',
      customer_id: rule.customer_id ? String(rule.customer_id) : '',
      customer_segment: normalizeCustomerSegmentCode(rule.customer_segment) || '',
      priority: rule.priority || 'medium',
      base_price: rule.base_price?.toString() || '',
      discount_percentage: rule.discount_percentage?.toString() || '',
      discount_amount: rule.discount_amount?.toString() || '',
      final_price: rule.final_price?.toString() || '',
      min_quantity: rule.min_quantity?.toString() || '',
      max_quantity: rule.max_quantity?.toString() || '',
      min_amount: rule.min_amount?.toString() || '',
      max_amount: rule.max_amount?.toString() || '',
      start_date: rule.start_date || '',
      end_date: rule.end_date || '',
      valid_days: rule.valid_days || '',
      usage_limit: rule.usage_limit?.toString() || '',
      usage_count: rule.usage_count?.toString() || '',
      stackable: rule.stackable || 'no',
      promo_code: rule.promo_code || '',
      conditions: rule.conditions || '',
      notes: rule.notes || '',
      created_by: rule.created_by || '',
      approved_by: rule.approved_by || '',
      price_includes_vat: rule.price_includes_vat || 'exclusive',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/ventes/prix-remises/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'قاعدة سعر' }));
        loadPriceRules();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('error_delete', { defaultValue: 'تعذّر حذف قاعدة السعر' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRules.length }))) {
      try {
        const deleteCount = selectedRules.length;
        await Promise.all(selectedRules.map(id => localApiAdapter.delete(`/ventes/prix-remises/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'قاعدة سعر' }));
        setSelectedRules([]);
        loadPriceRules();
      } catch (error) {
        console.error('Error deleting rules:', error);
        notifyError(showNotify, p('error_bulk_delete', { defaultValue: 'تعذّر حذف القواعد المحددة' }));
      }
    }
  };

  const openCreatePriceRuleModal = () => {
    if (!ventesConfig.enablePriceRules) return;
    setEditingRule(null);
    setFormTab('rule');
    setFormData({
      rule_reference: ventesConfig.autoGeneratePriceRuleReference !== false ? generatePriceRuleRef() : '',
      rule_name: '',
      rule_type: 'discount',
      discount_type: ventesConfig.defaultDiscountType || 'percentage',
      status: 'active',
      product_name: '',
      product_category: '',
      customer_name: '',
      customer_segment: '',
      priority: 'medium',
      base_price: '',
      discount_percentage: '',
      discount_amount: '',
      final_price: '',
      min_quantity: '',
      max_quantity: '',
      min_amount: '',
      max_amount: '',
      start_date: '',
      end_date: '',
      valid_days: '',
      usage_limit: '',
      usage_count: '',
      stackable: ventesConfig.allowStackableDiscounts ? 'yes' : 'no',
      promo_code: '',
      conditions: '',
      notes: '',
      created_by: '',
      approved_by: '',
      price_includes_vat: ventesConfig.defaultPriceIncludesVat ? 'inclusive' : 'exclusive',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ 
      rule_reference: '', 
      rule_name: '',
      rule_type: 'discount',
      discount_type: 'percentage',
      status: 'active',
      product_name: '',
      product_category: '',
      customer_name: '',
      customer_id: '',
      customer_segment: '',
      priority: 'medium',
      base_price: '',
      discount_percentage: '',
      discount_amount: '',
      final_price: '',
      min_quantity: '',
      max_quantity: '',
      min_amount: '',
      max_amount: '',
      start_date: '',
      end_date: '',
      valid_days: '',
      usage_limit: '',
      usage_count: '',
      stackable: 'no',
      promo_code: '',
      conditions: '',
      notes: '',
      created_by: '',
      approved_by: '',
      price_includes_vat: 'exclusive',
    });
    setEditingRule(null);
    setFormTab('rule');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'rule_reference', label: p('col_reference') },
      { key: 'rule_name', label: p('col_name') },
      { key: 'rule_type', label: p('col_type') },
      { key: 'discount_percentage', label: p('col_discount_pct') },
      { key: 'status', label: p('status_label') },
      { key: 'start_date', label: p('col_start_date') },
      { key: 'end_date', label: p('col_end_date') }
    ];
    const fileName = `prix_remises_${new Date().toISOString().split('T')[0]}`;
    exportToExcel(filteredRules, headers, `${fileName}.xlsx`);
  };

  const handleImport = () => {
    const headers = [
      { key: 'rule_reference', label: p('col_reference') },
      { key: 'rule_name', label: p('col_name') },
      { key: 'rule_type', label: p('col_type') },
      { key: 'discount_percentage', label: p('col_discount_pct') },
    ];
    const schema = {
      requiredFields: ['rule_reference', 'rule_name'],
      validators: {
        discount_percentage: (value) => !value || !isNaN(parseFloat(value))
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/ventes/prix-remises', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadPriceRules();
          alert(`${p('import_done_title')}\n\n${p('import_total', { count: result.totalCount })}\n${p('import_success_count', { count: successCount })}`);
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('error_import'));
      }
    }, '.csv,.xlsx,.xls');
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRules([]);
    } else {
      setSelectedRules(filteredRuleIds);
    }
  };
  
  const handleSelectRule = (id) => { 
    setSelectedRules(selectedRules.includes(id) ? selectedRules.filter(rId => rId !== id) : [...selectedRules, id]); 
  };

  const clickableStatusOptions = useMemo(
    () => [
      { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
      { value: 'scheduled', label: p('status_scheduled'), icon: Clock, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
      { value: 'expired', label: p('status_expired'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
      { value: 'inactive', label: p('status_inactive'), icon: AlertCircle, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
    ],
    [t],
  );

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/ventes/prix-remises/${recordId}`, { status: newStatus });
      await loadPriceRules();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: p('priority_low'), style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
      medium: { label: p('priority_medium'), style: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
      high: { label: p('priority_high'), style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
      critical: { label: p('priority_critical'), style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${config.style}`}>{config.label}</span>;
  };

  const renderPriceWithVat = (amount, includesVat) => {
    const n = parseFloat(amount) || 0;
    if (!n) return formatBrandMoney(0);
    if (includesVat === 'inclusive') {
      return (
        <span className="text-green-300 font-semibold" title={p('price_vat_inclusive')}>
          {formatBrandMoney(n)}
        </span>
      );
    }
    return (
      <span className="text-green-300 font-semibold" title={p('vat_breakdown', {
        excl: formatBrandMoney(amountExclVat(n)),
        vat: formatBrandMoney(vatFromTtc(n)),
        rate: vatRate,
      })}>
        {formatBrandMoney(n)}
      </span>
    );
  };


  const listStats = useMemo(
    () => mapVentesStatCards([
      { key: 'total', label: p('stat_total_rules'), value: stats.total, icon: Tag, variant: 'copper' },
      { key: 'active', label: p('stat_active'), value: stats.active, icon: CheckCircle, variant: 'forest' },
      { key: 'scheduled', label: p('stat_scheduled'), value: stats.scheduled, icon: Clock, variant: 'copper' },
      { key: 'expired', label: p('stat_expired'), value: stats.expired, icon: XCircle, variant: 'forest' },
    ]),
    [stats, p],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
              <XCircle size={14} />{p('reset_filter')}
            </button>
          )}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3', 'lg:grid-cols-4')}>{distributionInfo.data.map((item) => (
            <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`} title={p('filter_click_title', { name: item.name })}>
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }} />
              </div>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
      <VentesConfigNotice
        message={!ventesConfig.enablePriceRules ? p('notice_price_rules_disabled', { defaultValue: 'الأسعار والخصومات معطّلة — فعّلها من إعدادات المبيعات ← الأسعار والخصومات.' }) : null}
      />
      <VentesListPageChrome
        t={t}
        p={p}
        qawaem={qawaem}
        isDashboardLightMode={isDashboardLightMode}
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Tag}
        listStats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildVentesBulkDeleteFilterAction({
          selectedCount: selectedRules.length,
          onDelete: handleBulkDelete,
          label: p('bulk_delete_confirm', { count: selectedRules.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search') })}
        onCreate={ventesConfig.enablePriceRules ? openCreatePriceRuleModal : undefined}
        createLabel={p('create')}
        importExportState={{ showImportDropdown, setShowImportDropdown, showExportDropdown, setShowExportDropdown, importDropdownRef, exportDropdownRef }}
        importExportHandlers={{ onImport: handleImport, onImportCSV: handleImport, onImportExcel: handleImport, onExportExcel: handleExport, onExportCSV: handleExport }}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                        title={allRowsSelected ? p('deselect_all', { defaultValue: t('common.deselect_all') }) : p('select_all', { defaultValue: t('common.select_all') })}
                      />
                    </div>
                  </th>
                  
                  <TableColumnHeaderFilter columnKey="rule_reference" label={p('col_reference')} value={filters.rule_reference || ''} onChange={(v) => setFilter('rule_reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  {showCol('rule_name') ? (
                    <TableColumnHeaderFilter columnKey="rule_name" label={p('col_name')} value={filters.rule_name || ''} onChange={(v) => setFilter('rule_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('rule_type') ? (
                    <TableColumnHeaderFilter columnKey="rule_type" label={p('col_type')} value={filters.rule_type || ''} onChange={(v) => setFilter('rule_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('product_name') ? (
                    <TableColumnHeaderFilter columnKey="product_name" label={p('col_product')} value={filters.product_name || ''} onChange={(v) => setFilter('product_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('discount_percentage') ? (
                    <TableColumnHeaderFilter columnKey="discount_percentage" label={p('col_discount')} value={filters.discount_percentage || ''} onChange={(v) => setFilter('discount_percentage', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('start_date') ? (
                    <TableColumnHeaderFilter columnKey="start_date" label={p('col_period')} value={filters.start_date || ''} onChange={(v) => setFilter('start_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('usage_count') ? (
                    <TableColumnHeaderFilter columnKey="usage_count" label={p('col_usage')} value={filters.usage_count || ''} onChange={(v) => setFilter('usage_count', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('priority') ? (
                    <TableColumnHeaderFilter columnKey="priority" label={p('col_priority')} value={filters.priority || ''} onChange={(v) => setFilter('priority', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('status') ? (
                    <TableColumnHeaderFilter columnKey="status" label={p('status_label')} kind="select" options={ruleStatusFilterOptions} value={filters.status || ''} onChange={(v) => setFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedRules.length > 0 ? (
                  displayedRules.map((rule, index) => (
                    <tr 
                      key={rule.id} 
                      className={`hover:bg-white/5 transition-colors ${selectedRules.includes(rule.id) ? 'bg-white/10' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRules.includes(rule.id)}
                            onChange={() => handleSelectRule(rule.id)}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{rule.rule_reference}</span>
                      </td>
                      {showCol('rule_name') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{rule.rule_name}</span>
                            {rule.promo_code && <span className="text-white/50 text-xs mt-0.5">{p('promo_code_prefix')} {rule.promo_code}</span>}
                          </div>
                        </td>
                      ) : null}
                      {showCol('rule_type') ? (
                        <td className="px-6 py-4">
                          <span className="text-white/80">{getRuleTypeLabel(rule.rule_type)}</span>
                        </td>
                      ) : null}
                      {showCol('product_name') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{rule.product_name || p('card_label_all')}</span>
                            {rule.product_category && <span className="text-white/50 text-xs mt-0.5">{rule.product_category}</span>}
                          </div>
                        </td>
                      ) : null}
                      {showCol('discount_percentage') ? (
                        <td className="px-6 py-4 text-center">
                          {rule.discount_percentage ? (
                            <span className="text-green-300 font-semibold">{rule.discount_percentage}%</span>
                          ) : rule.discount_amount ? (
                            renderPriceWithVat(rule.discount_amount, rule.price_includes_vat)
                          ) : (
                            <span className="text-white/50">-</span>
                          )}
                        </td>
                      ) : null}
                      {showCol('start_date') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{rule.start_date ? new Date(rule.start_date).toLocaleDateString(dateLocale) : '-'}</span>
                            <span className="text-white/50 text-xs mt-0.5">{p('period_to', { date: rule.end_date ? new Date(rule.end_date).toLocaleDateString(dateLocale) : '-' })}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('usage_count') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className="text-white font-semibold">{rule.usage_count || 0}</span>
                            {rule.usage_limit && <span className="text-white/50 text-xs mt-0.5">/ {rule.usage_limit}</span>}
                          </div>
                        </td>
                      ) : null}
                      {showCol('priority') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">{getPriorityBadge(rule.priority)}</div>
                        </td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={rule.status}
                              recordId={rule.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRule(rule); setShowViewModal(true); }}
                          onEdit={() => handleEdit(rule)}
                          onDelete={() => handleDelete(rule.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Tag size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_rules')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View - Condensed */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedRules.length > 0 ? (
              displayedRules.map((rule, index) => (
                <motion.div
                  key={rule.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Tag size={20} className="text-blue-400" />
                          {rule.rule_name}
                        </h3>
                        <div className="flex gap-2">
                          <QawaemClickableStatusBadge
                            status={rule.status}
                            recordId={rule.id}
                            options={clickableStatusOptions}
                            onChange={handleStatusChange}
                          />
                          {getPriorityBadge(rule.priority)}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_information')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_label_reference')}</span> {rule.rule_reference}</div>
                            <div><span className="font-medium">{p('card_label_type')}</span> {getRuleTypeLabel(rule.rule_type)}</div>
                            {rule.promo_code && <div><span className="font-medium">{p('promo_code_prefix').replace(':', '')}</span> {rule.promo_code}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('col_product')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_label_name')}</span> {rule.product_name || p('card_label_all')}</div>
                            <div><span className="font-medium">{p('card_label_category')}</span> {rule.product_category || p('card_label_all_categories')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('col_discount')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {rule.discount_percentage && <div><span className="font-medium">{p('card_label_percentage')}</span> <span className="text-green-300">{rule.discount_percentage}%</span></div>}
                            {rule.discount_amount && <div><span className="font-medium">{p('card_label_amount')}</span> {renderPriceWithVat(rule.discount_amount, rule.price_includes_vat)}</div>}
                            {rule.final_price && <div><span className="font-medium">{p('card_label_final_price')}</span> {renderPriceWithVat(rule.final_price, rule.price_includes_vat)}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('col_usage')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_label_used')}</span> {rule.usage_count || 0}</div>
                            {rule.usage_limit && <div><span className="font-medium">{p('card_label_limit')}</span> {rule.usage_limit}</div>}
                            <div><span className="font-medium">{p('card_label_stackable')}</span> {rule.stackable === 'yes' ? p('yes') : p('no')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRule(rule); setShowViewModal(true); }}
                      onEdit={() => handleEdit(rule)}
                      onDelete={() => handleDelete(rule.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Tag size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_rules')}</p>
              </div>
            )}
          </div>
        )}
      </VentesListPageChrome>

      {showViewModal && viewingRule ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_rule_details')}
        icon={Eye}
        maxWidth="900px"
        testId="prix-remises-view-modal"
      >
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <h3 className="text-white font-semibold mb-4">{p('section_information')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-sm mb-1">{p('col_reference')}</p><p className="text-white font-medium">{viewingRule.rule_reference}</p></div>
                  <div><p className="text-white/60 text-sm mb-1">{p('col_name')}</p><p className="text-white font-medium">{viewingRule.rule_name}</p></div>
                  <div><p className="text-white/60 text-sm mb-1">{p('col_type')}</p><p className="text-white font-medium">{getRuleTypeLabel(viewingRule.rule_type)}</p></div>
                  {viewingRule.customer_segment ? (
                    <div><p className="text-white/60 text-sm mb-1">{p('field_customer_segment')}</p><p className="text-white font-medium">{getCustomerSegmentLabel(viewingRule.customer_segment)}</p></div>
                  ) : null}
                  <div><p className="text-white/60 text-sm mb-1">{p('status_label')}</p><div className="flex"><QawaemClickableStatusBadge status={viewingRule.status} recordId={viewingRule.id} options={clickableStatusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status') })} /></div></div>
                </div>
              </div>
              {viewingRule.conditions && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-2">{p('section_conditions')}</h3>
                  <p className="text-white/80 whitespace-pre-wrap">{viewingRule.conditions}</p>
                </div>
              )}
            </div>
      </QawaemErpViewModal>
      ) : null}

      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingRule ? p('modal_edit_rule') : p('modal_new_rule')}
        icon={Tag}
        showSaveButton
        formId="rule-form"
        saveLabel={editingRule ? p('update') : p('create')}
        maxWidth="1000px"
        testId="prix-remises-crud-modal"
        tabs={priceFormTabs}
        activeTab={formTab}
        onTabChange={setFormTab}
        tabsAriaLabel={editingRule ? p('modal_edit_rule') : p('modal_new_rule')}
      >
        <form id="rule-form" onSubmit={handleSubmit}>
          {formTab === 'rule' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_rule_reference')} <span className="qawaem-required-mark">*</span></label><input type="text" required value={formData.rule_reference} onChange={(e) => setFormData({ ...formData, rule_reference: e.target.value })} className={VENTES_REF_INPUT_CLASS} placeholder={p('ph_rule_reference')} dir="ltr" /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_rule_name')} <span className="qawaem-required-mark">*</span></label><input type="text" required value={formData.rule_name} onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30" placeholder={p('ph_rule_name')} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_rule_type')}</label><CustomSelect value={formData.rule_type} onChange={(value) => setFormData({ ...formData, rule_type: value })} options={ruleTypeOptions} searchable={false} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_discount_type')}</label><CustomSelect value={formData.discount_type} onChange={(value) => setFormData({ ...formData, discount_type: value })} options={discountTypeOptions} searchable={false} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_status')}</label><CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions} searchable={false} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_priority')}</label><CustomSelect value={formData.priority} onChange={(value) => setFormData({ ...formData, priority: value })} options={priorityOptions} searchable={false} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_product')}</label><input type="text" value={formData.product_name} onChange={(e) => setFormData({ ...formData, product_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30" placeholder={p('ph_product_all')} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_product_category')}</label><CustomSelect value={formData.product_category} onChange={(value) => setFormData({ ...formData, product_category: value })} options={productCategoryOptions} creatable placeholder={p('ph_select_or_type')} /></div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_customer')}</label>
                <CustomSelect
                  value={formData.customer_id ? String(formData.customer_id) : ''}
                  onChange={(clientId) => {
                    if (!clientId) {
                      setFormData({ ...formData, customer_id: '', customer_name: '' });
                      return;
                    }
                    const client = availableClients.find((c) => String(c.id) === String(clientId));
                    setFormData({
                      ...formData,
                      customer_id: String(clientId),
                      customer_name: client?.company_name || client?.name || '',
                    });
                  }}
                  options={availableClients.map((client) => ({
                    value: String(client.id),
                    label: `${client.company_name || client.name || p('field_customer')}${client.contact_person ? ` — ${client.contact_person}` : ''}`,
                  }))}
                  placeholder={p('ph_customer_all')}
                  searchable
                />
              </div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_customer_segment')}</label><CustomSelect value={formData.customer_segment} onChange={(value) => setFormData({ ...formData, customer_segment: value })} options={customerSegmentOptions} creatable placeholder={p('ph_select_or_type')} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_promo_code')}</label><input type="text" value={formData.promo_code} onChange={(e) => setFormData({ ...formData, promo_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30" placeholder={p('ph_promo_code')} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_stackable')}</label><CustomSelect value={formData.stackable} onChange={(value) => setFormData({ ...formData, stackable: value })} options={stackableOptions} searchable={false} /></div>
            </div>
          )}
          {formTab === 'pricing' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_price_includes_vat')}</label><CustomSelect value={formData.price_includes_vat} onChange={(value) => setFormData({ ...formData, price_includes_vat: value })} options={priceIncludesVatOptions} searchable={false} /><p className="text-white/50 text-xs mt-1">{p('vat_rate_label', { rate: vatRate })}</p></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_base_price')}</label><CustomNumberInput value={formData.base_price} onChange={(value) => setFormData({ ...formData, base_price: value })} placeholder={p('placeholder_decimal')} step={amountStep} min={0} decimals={2} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_discount_pct')}</label><CustomNumberInput value={formData.discount_percentage} onChange={(value) => setFormData({ ...formData, discount_percentage: value })} placeholder={p('placeholder_zero')} step={1} min={0} max={100} decimals={2} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_discount_amount')}</label><CustomNumberInput value={formData.discount_amount} onChange={(value) => setFormData({ ...formData, discount_amount: value })} placeholder={p('placeholder_decimal')} step={amountStep} min={0} decimals={2} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_final_price')}</label><CustomNumberInput value={formData.final_price} onChange={(value) => setFormData({ ...formData, final_price: value })} placeholder={p('placeholder_decimal')} step={amountStep} min={0} decimals={2} /></div>
            </div>
          )}
          {formTab === 'conditions' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <p className="md:col-span-3 text-white/60 text-sm">{volumeTierHint}</p>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_min_qty')}</label><CustomNumberInput value={formData.min_quantity} onChange={(value) => setFormData({ ...formData, min_quantity: value })} placeholder={p('placeholder_zero')} step={1} min={0} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_max_qty')}</label><CustomNumberInput value={formData.max_quantity} onChange={(value) => setFormData({ ...formData, max_quantity: value })} placeholder={p('placeholder_zero')} step={1} min={0} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_min_amount')}</label><CustomNumberInput value={formData.min_amount} onChange={(value) => setFormData({ ...formData, min_amount: value })} placeholder={p('placeholder_decimal')} step={amountStep} min={0} decimals={2} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_max_amount')}</label><CustomNumberInput value={formData.max_amount} onChange={(value) => setFormData({ ...formData, max_amount: value })} placeholder={p('placeholder_decimal')} step={amountStep} min={0} decimals={2} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_usage_limit')}</label><CustomNumberInput value={formData.usage_limit} onChange={(value) => setFormData({ ...formData, usage_limit: value })} placeholder={p('placeholder_zero')} step={1} min={0} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_usage_count')}</label><CustomNumberInput value={formData.usage_count} onChange={(value) => setFormData({ ...formData, usage_count: value })} placeholder={p('placeholder_zero')} step={1} min={0} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_start_date')}</label><CustomDatePicker value={formData.start_date} onChange={(date) => setFormData({ ...formData, start_date: date })} placeholder={p('ph_select_date')} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_end_date')}</label><CustomDatePicker value={formData.end_date} onChange={(date) => setFormData({ ...formData, end_date: date })} placeholder={p('ph_select_date')} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_valid_days')}</label><input type="text" value={formData.valid_days} onChange={(e) => setFormData({ ...formData, valid_days: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30" placeholder={p('ph_valid_days')} /></div>
              <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_created_by')}</label><input type="text" value={formData.created_by} onChange={(e) => setFormData({ ...formData, created_by: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30" placeholder={p('field_created_by')} /></div>
              <div className="md:col-span-3"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_conditions')}</label><textarea value={formData.conditions} onChange={(e) => setFormData({ ...formData, conditions: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]" placeholder={p('ph_conditions')} /></div>
              <div className="md:col-span-3"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[120px]" placeholder={p('ph_notes')} /></div>
            </div>
          )}
        </form>
      </QawaemErpCrudModal>
    </>
  );
};

export default PrixRemisesPage;

